import { Button } from "@/components/ui/button.tsx";
import { TypingText } from "@/components/ui/TypingText.tsx";
import { VerificationCodeInput } from "@/components/auth/VerificationCodeInput.tsx";
import { useOnboardingAnimation } from "@/hooks/useOnboardingAnimation.tsx";
import { isAxiosError } from "axios";
import { ArrowUp } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { apiErrorMessage, loggableRequestError } from "@/shared/apiErrorMessage.ts";
import { confirmationToast } from "../../components/ui/toast.tsx";
import { PhoneAlreadyTakenPanel } from "./PhoneAlreadyTakenPanel.tsx";
import { useResendPhoneCode, useVerifyPhoneCode } from "./hooks/usePhoneVerification.tsx";

export const VerifyPhoneStep = ({
  onBack,
  onDone,
  returnTo = null,
}: {
  /** Return to the number-entry screen. */
  onBack: () => void;
  /** The number is verified but no setup thread came back. */
  onDone: () => void;
  /** Where a step-up started; verifying goes back there rather than to setup. */
  returnTo?: string | null;
}) => {
  const [, setLocation] = useLocation();
  const [error, setError] = useState("");
  const [phoneAlreadyTaken, setPhoneAlreadyTaken] = useState(false);
  const verifyPhoneCode = useVerifyPhoneCode();
  const resendCode = useResendPhoneCode();

  const {
    animationState,
    handleFirstTextComplete,
    handleSecondTextComplete,
    handleThirdTextComplete,
  } = useOnboardingAnimation({ isActive: true });

  const handleVerify = async (code: string) => {
    try {
      const { onboardingThreadId } = await verifyPhoneCode.mutateAsync({ code });

      // A step-up: the account was already set up and only had to prove its
      // number before an action. Back to that action, not to setup.
      if (returnTo) {
        setLocation(returnTo, { replace: true });
        return;
      }

      // The rest of setup happens as a conversation, so hand the user to the
      // thread. The server decides whether this account has one; an id arriving
      // here is the whole of that decision.
      //
      // `replace`, so the verify screen doesn't sit behind the thread in
      // history and browser-back from the thread lands on home rather than
      // partway through a flow the user has finished.
      if (onboardingThreadId !== undefined) {
        setLocation(`/threads/${onboardingThreadId}`, { replace: true });
        return;
      }

      onDone();
    } catch (err) {
      if (isAxiosError(err) && err.response?.data?.code === "PHONE_ALREADY_TAKEN") {
        setPhoneAlreadyTaken(true);
        setError(err.response.data.error);
      } else if (isAxiosError(err) && err.response?.data?.code === "TOO_MANY_ATTEMPTS") {
        // The server names the wait; "try again" would send them straight
        // back into the lockout.
        setError(err.response.data.error);
      } else {
        // Never the error itself: its request body is the code just typed.
        console.error("Failed to verify phone code:", loggableRequestError(err));
        setError("Your number could not be verified. Please try again.");
      }
    }
  };

  const handleResend = async () => {
    try {
      await resendCode.mutateAsync();
      confirmationToast("Code resent");
      setError("");
    } catch (err) {
      // A refused resend is expected when too many codes went out; anything
      // else is worth a log line. Either way the user hears why.
      if (!(isAxiosError(err) && err.response?.data?.code === "TOO_MANY_SENDS")) {
        console.error("Failed to resend verification code:", loggableRequestError(err));
      }
      setError(apiErrorMessage(err) ?? "We couldn't resend your code. Please try again.");
    }
  };

  return (
    <div className="flex flex-col">
      <div className="space-y-6 flex-shrink-0 md:pb-6">
        <div className="text-base text-zinc-900 leading-6">
          {animationState.showFirstText && (
            <TypingText
              text="We just sent you a text with a 6-digit verification code."
              onComplete={handleFirstTextComplete}
            />
          )}
        </div>
        <div className="text-4xl font-serif text-zinc-900 leading-tight">
          {animationState.showSecondText && (
            <TypingText text="Enter the verification code" onComplete={handleSecondTextComplete} />
          )}
        </div>
        <div className="text-base text-zinc-900 leading-6">
          {animationState.showThirdText && (
            <TypingText
              text="I just want to be sure your number is reachable and to ensure it's really you."
              onComplete={handleThirdTextComplete}
            />
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col md:w-1/2 pt-6 px-1">
        <div
          className={`flex flex-col space-y-6 transition-all duration-700 ${
            animationState.complete ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          {phoneAlreadyTaken ? (
            <PhoneAlreadyTakenPanel error={error} back={onBack} />
          ) : (
            <>
              <VerificationCodeInput
                onComplete={handleVerify}
                disabled={verifyPhoneCode.isPending}
                error={error}
                onErrorClear={() => setError("")}
                inputClassName="w-12 h-13 text-center text-lg font-medium border border-zinc-300 rounded-xl bg-white/70 backdrop-blur-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400 outline-none"
              />

              <p className="text-sm text-zinc-400 italic">Enter your 6-digit code</p>

              <div className="flex items-center gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleResend}
                  disabled={resendCode.isPending}
                  className="text-sm text-zinc-900 hover:text-zinc-700 disabled:opacity-50"
                  data-statsig-name="onboarding-verify-phone-resend"
                >
                  {resendCode.isPending ? "Sending..." : "Resend code"}
                </Button>

                <Button
                  variant="outline"
                  onClick={onBack}
                  data-statsig-name="onboarding-verify-phone-back"
                >
                  <div className="flex items-center gap-2">
                    Back
                    <ArrowUp className="w-4 h-4" />
                  </div>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
